import { getElement } from '../utils/dom-utils';
import {
  getPresets,
  savePreset,
  loadPreset,
  deletePreset
} from '../services/preset.service';
import { getFilterRoot, setFilterRoot } from '../state/filter-state';
import { renderFilterTree } from '../filter/filter-renderer';
import { ApiError } from '../types/api.types';

let onPresetLoad: (() => void) | null = null;

export function setPresetLoadCallback(callback: () => void): void {
  onPresetLoad = callback;
}

async function renderPresetOptions(): Promise<string> {
  const presets = await getPresets();
  if (presets.length === 0) {
    return '<option value="">Keine Presets vorhanden</option>';
  }
  return '<option value="">Preset wählen...</option>' +
    presets.map(p => `<option value="${p.id}">${p.name}</option>`).join('');
}

export async function renderPresetManager(): Promise<string> {
  return `
    <div class="preset-manager">
      <div class="preset-header">Filter-Presets</div>
      <div class="preset-select-row">
        <select id="presetSelect" class="preset-select">
          ${await renderPresetOptions()}
        </select>
      </div>
      <div class="preset-actions">
        <button id="loadPresetBtn" class="preset-btn preset-btn-load" disabled>Laden</button>
        <button id="savePresetBtn" class="preset-btn preset-btn-save">Speichern</button>
        <button id="deletePresetBtn" class="preset-btn preset-btn-delete" disabled>Löschen</button>
      </div>
      <div id="presetStatus" class="preset-status"></div>
    </div>
  `;
}

async function updatePresetSelect(): Promise<void> {
  const select = getElement<HTMLSelectElement>('presetSelect');
  if (select) {
    select.innerHTML = await renderPresetOptions();
  }
  updateButtonStates();
}

function updateButtonStates(): void {
  const select = getElement<HTMLSelectElement>('presetSelect');
  const loadBtn = getElement<HTMLButtonElement>('loadPresetBtn');
  const deleteBtn = getElement<HTMLButtonElement>('deletePresetBtn');

  const hasSelection = select && select.value !== '';

  if (loadBtn) loadBtn.disabled = !hasSelection;
  if (deleteBtn) deleteBtn.disabled = !hasSelection;
}

function setButtonsEnabled(enabled: boolean): void {
  const loadBtn = getElement<HTMLButtonElement>('loadPresetBtn');
  const saveBtn = getElement<HTMLButtonElement>('savePresetBtn');
  const deleteBtn = getElement<HTMLButtonElement>('deletePresetBtn');
  const select = getElement<HTMLSelectElement>('presetSelect');

  if (loadBtn) loadBtn.disabled = !enabled || !(select && select.value !== '');
  if (saveBtn) saveBtn.disabled = !enabled;
  if (deleteBtn) deleteBtn.disabled = !enabled || !(select && select.value !== '');
  if (select) select.disabled = !enabled;
}

function showStatus(message: string, isError: boolean = false): void {
  const status = getElement<HTMLDivElement>('presetStatus');
  if (status) {
    status.textContent = message;
    status.className = `preset-status ${isError ? 'preset-status-error' : 'preset-status-success'}`;
    status.style.display = 'block';

    // Auto-hide after 3 seconds
    setTimeout(() => {
      status.style.display = 'none';
    }, 3000);
  }
}

function showLoading(message: string): void {
  const saveBtn = getElement<HTMLButtonElement>('savePresetBtn');
  const loadBtn = getElement<HTMLButtonElement>('loadPresetBtn');
  const deleteBtn = getElement<HTMLButtonElement>('deletePresetBtn');

  if (saveBtn) saveBtn.textContent = message;
  if (loadBtn) loadBtn.textContent = message;
  if (deleteBtn) deleteBtn.textContent = message;

  setButtonsEnabled(false);
}

function hideLoading(): void {
  const saveBtn = getElement<HTMLButtonElement>('savePresetBtn');
  const loadBtn = getElement<HTMLButtonElement>('loadPresetBtn');
  const deleteBtn = getElement<HTMLButtonElement>('deletePresetBtn');

  if (saveBtn) saveBtn.textContent = 'Speichern';
  if (loadBtn) loadBtn.textContent = 'Laden';
  if (deleteBtn) deleteBtn.textContent = 'Löschen';

  setButtonsEnabled(true);
}

async function handleSavePreset(): Promise<void> {
  const name = prompt('Name für das Preset:');
  if (!name || !name.trim()) return;

  showLoading('Speichert...');

  try {
    await savePreset(name.trim(), getFilterRoot());
    await updatePresetSelect();
    showStatus('Preset gespeichert');
  } catch (error) {
    if (error instanceof ApiError) {
      showStatus(error.message, true);
    } else {
      showStatus('Fehler beim Speichern', true);
    }
  } finally {
    hideLoading();
  }
}

async function handleLoadPreset(): Promise<void> {
  const select = getElement<HTMLSelectElement>('presetSelect');
  if (!select || !select.value) return;

  showLoading('Lädt...');

  try {
    const loadedRoot = await loadPreset(select.value);
    if (loadedRoot) {
      setFilterRoot(loadedRoot);
      renderFilterTree();
      if (onPresetLoad) {
        onPresetLoad();
      }
      showStatus('Preset geladen');
    } else {
      showStatus('Preset nicht gefunden', true);
    }
  } catch (error) {
    if (error instanceof ApiError) {
      showStatus(error.message, true);
    } else {
      showStatus('Fehler beim Laden', true);
    }
  } finally {
    hideLoading();
  }
}

async function handleDeletePreset(): Promise<void> {
  const select = getElement<HTMLSelectElement>('presetSelect');
  if (!select || !select.value) return;

  const presets = await getPresets();
  const preset = presets.find(p => p.id === select.value);
  if (!preset) return;

  const confirmed = confirm(`Preset "${preset.name}" wirklich löschen?`);
  if (!confirmed) return;

  showLoading('Löscht...');

  try {
    await deletePreset(select.value);
    await updatePresetSelect();
    showStatus('Preset gelöscht');
  } catch (error) {
    if (error instanceof ApiError) {
      showStatus(error.message, true);
    } else {
      showStatus('Fehler beim Löschen', true);
    }
  } finally {
    hideLoading();
  }
}

export async function setupPresetManager(): Promise<void> {
  const select = getElement<HTMLSelectElement>('presetSelect');
  const loadBtn = getElement<HTMLButtonElement>('loadPresetBtn');
  const saveBtn = getElement<HTMLButtonElement>('savePresetBtn');
  const deleteBtn = getElement<HTMLButtonElement>('deletePresetBtn');

  if (select) {
    select.addEventListener('change', updateButtonStates);
  }

  if (loadBtn) {
    loadBtn.addEventListener('click', handleLoadPreset);
  }

  if (saveBtn) {
    saveBtn.addEventListener('click', handleSavePreset);
  }

  if (deleteBtn) {
    deleteBtn.addEventListener('click', handleDeletePreset);
  }

  updateButtonStates();
}
